import { ReactNode } from 'react';
import clsx from 'clsx';

interface Props {
  children: ReactNode;
  className?: string;
  color?: ColorVariant | 'success' | 'warning' | 'error';
  dense?: boolean;
  icon?: ReactNode;
  fullWidth?: boolean;
  size?: SizeVariant;
  square?: boolean;
  tooltip?: string;
  variant?: ComponentVariant;
}

export const Tag = (props: Props) => {
  const {
    children,
    className = '',
    color = 'primary',
    dense = false,
    fullWidth = false,
    icon = null,
    size = 'medium',
    square = false,
    tooltip = null,
    variant = 'filled',
    ...rest
  } = props;
  const classes = useStyles();

  const startIcon = () => {
    if (!icon) return;
    return icon;
  };

  return (
    <span
      className={clsx(
        className,
        classes.default,
        classes.radius[square ? 'square' : 'rounded'],
        classes.padding[dense ? 'dense' : 'default'],
        classes.size[size],
        classes.color[color],
        {
          [classes.fit]: !fullWidth,
          'w-full': fullWidth,
          [classes.variant.outlined]: variant === 'outlined',
        }
      )}
      {...rest}
    >
      {startIcon()}
      {children}
    </span>
  );
};

const useStyles = () => ({
  default: 'flex whitespace-nowrap items-center justify-center gap-1 h-fit',
  color: {
    primary: 'bg-primary text-white',
    secondary: 'bg-tags-secondary-background text-tags-secondary-color',
    success: 'bg-tags-success-background text-tags-success-color',
    warning: 'bg-tags-warning-background text-tags-warning-color',
    error: 'bg-tags-error-background text-tags-error-color',
  },
  fit: 'w-fit',
  radius: {
    rounded: 'rounded-full',
    square: 'rounded',
  },
  padding: {
    default: 'px-4 py-0.5',
    dense: 'px-2 py-0.5',
  },
  size: {
    small: 'text-xs children:p-1',
    medium: 'text-sm children:p-0.5',
    large: 'text-base children:p-0',
  },
  variant: {
    outlined:
      '!bg-transparent border border-gray-500 text-gray-500 dark:border-white dark:text-white',
  },
});
